import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CreditCard, Download, TrendingUp, Wallet } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/format';

export const Route = createFileRoute('/_authed/billing/')({
  component: BillingPage,
});

interface Tx {
  id: string;
  amount: number;
  currency: string;
  method: string;
  status: 'succeeded' | 'pending' | 'refunded' | 'failed';
  customer: string;
  date: number;
}

const mock: {
  mrr: number;
  arr: number;
  arpu: number;
  refunds: number;
  transactions: Tx[];
  series: Array<{ m: string; value: number }>;
} = {
  mrr: 18920,
  arr: 227040,
  arpu: 41.2,
  refunds: 320,
  transactions: Array.from({ length: 12 }, (_, i) => ({
    id: `tx_${i}`,
    amount: 49 + Math.round(Math.random() * 100),
    currency: 'EUR',
    method: ['card', 'paypal', 'transfer'][i % 3] as string,
    status: (['succeeded', 'pending', 'refunded', 'failed'] as const)[i % 4],
    customer: `cliente${i}@empresa.com`,
    date: Date.now() - i * 86400_000,
  })),
  series: Array.from({ length: 12 }, (_, i) => ({
    m: `M${i + 1}`,
    value: 14000 + Math.round(Math.sin(i / 2) * 2500 + i * 200),
  })),
};

function BillingPage() {
  const { data } = useQuery({
    queryKey: ['billing'],
    queryFn: async () => {
      try {
        return await api.get<typeof mock>('/admin/billing');
      } catch {
        return mock;
      }
    },
  });
  const d = data ?? mock;

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Facturación</h1>
        <p className="text-muted-foreground">Ingresos, transacciones y reembolsos</p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Stat label="MRR" value={formatCurrency(d.mrr)} icon={Wallet} />
        <Stat label="ARR" value={formatCurrency(d.arr)} icon={TrendingUp} />
        <Stat label="ARPU" value={formatCurrency(d.arpu)} icon={CreditCard} />
        <Stat label="Reembolsos (mes)" value={formatCurrency(d.refunds)} icon={CreditCard} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Ingresos recurrentes (12 meses)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d.series}>
                <XAxis dataKey="m" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '0.5rem',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="hsl(199 89% 48%)"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Transacciones recientes</CardTitle>
          <CardDescription>Últimas operaciones de pago</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Método</TableHead>
                <TableHead>Importe</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {d.transactions.map((tx) => (
                <TableRow key={tx.id}>
                  <TableCell className="text-muted-foreground">
                    {formatDate(tx.date, 'es')}
                  </TableCell>
                  <TableCell className="font-medium">{tx.customer}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{tx.method}</Badge>
                  </TableCell>
                  <TableCell>
                    {formatCurrency(tx.amount, tx.currency)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        tx.status === 'succeeded'
                          ? 'success'
                          : tx.status === 'pending'
                            ? 'warning'
                            : tx.status === 'refunded'
                              ? 'secondary'
                              : 'destructive'
                      }
                    >
                      {tx.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" aria-label="Descargar">
                      <Download className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {label}
        </CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}
